import { ContainerSize } from '../interfaces';
import { OFFER_STATUS, calculateDealerBoost, getOfferCertificateUrl } from '../utils/utils.offer';

import { toBase64, toUrlDisplayNamePath } from './utils.url';
import { MSM_MILEAGE_ERRORS } from './utils.vehicle';

export const VIN_LENGTH = 17;
export const STATUS_SUCCESS = 200000;
export const VIN_ERRORS = {
  VIN_LENGTH_ERROR: 'A VIN must contain exactly 17 characters.',
  VIN_NOT_FOUND_ERROR: 'VIN not found. Please re-enter.',
};
export const LICENSE_PLATE_ERROR = {
  LICENSE_PLATE_NOT_FOUND: `We couldn't find that license plate. Please re-enter it or select another source`,
  LICENSE_PLATE_MISSING: 'Enter your License Plate and State.',
};
export const YMM_ERRORS = {
  YMM_VEHICLE_NOT_ELIGIBLE: 'Sorry, vehicle not eligible.',
  YMM_MISSING: 'Please select year make and model',
};
export const MSM_STYLE_MISSING = 'Please select a style';
export const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';
export const SOURCE_KBB = 'KBB';
export const LEVEL_EXPERT_APPRAISAL = 'ICO Expert Appraisal';
export const LEVEL_BUYING_CENTER = 'ICO Buying Center';
export const LICENSE_PLATE_KEY = 'lp';
export const LICENSE_PLATE_VALUE = 'licensePlate';
export const DEFAULT_TAB_LIST = 'vin-lp-ymm';
export const SCANNER_CANCEL_PATH = 'assets/scanner-cancel.png';
export const OVERLAY_PATH = 'assets/overlay.png';

export interface YMMSuggestion {
  makeId: number;
  makeName: string;
  modelId: number;
  modelName: string;
  trimId: number;
  trimName: string;
  vehicleName: string;
  vehicleId: number;
  displayName: string;
  yearId: number;
}

export interface FunnelUrlParams {
  offerCode: string;
  dealerId: string;
  groupId: string;
  vin: string;
  yearId: number;
  make: string;
  model: string;
  vehicleId: number;
  mileage: number;
  voiVin: string;
  sessionId: string;
  dealershipAcceptsRv: boolean;
  cobrandingGroupAcceptsRv: boolean;
}

// eslint-disable-next-line @stencil-community/ban-exported-const-enums
export enum BannerStep {
  FormSelector = 1,
  MSM = 2,
  NotQualify = 3,
  ThankYou = 4,
  UnSubscribed = 5,
  FeatureOff = 6,
  Continue = 7,
  Eligible = 8,
  Review = 9,
  Inspection = 10,
}

// eslint-disable-next-line @stencil-community/ban-exported-const-enums
export enum BannerInstallationMode {
  Offsite = 'offsite',
  PopUp = 'popup',
}

// eslint-disable-next-line @stencil-community/ban-exported-const-enums
export enum BannerStepStorage {
  BannerCurrenStep = 'BannerCurrenStep',
  UrlCurrentStep = 'UrlCurrentStep',
  OfferCertificate = 'OfferCertificate',
}

// eslint-disable-next-line @stencil-community/ban-exported-const-enums
export enum ComponentDirection {
  Default = 1,
  Heading = 2,
}

export const consistentStoreAttributes = [
  'installationMode',
  'offerCode',
  'dealerId',
  'groupId',
  'voiVin',
  'tabList',
  'defaultSelectedTab',
  'currentSelectedTab',
  'tabHeaders',
  'states',
  'zIndex',
  'vin',
  'inputVin',
  'mileage',
  'measurementId',
  'sdContainer',
  'oemDealerCode',
  'sessionId',
];

export const bannerMinSizes = {
  '5xs': 253,
  '4xs': 323,
  '3xs': 350,
  xs: 567,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1100,
  '2xl': 1536,
};

export function mapSuggestionsToValue(suggestions: YMMSuggestion[]) {
  return suggestions.map((s) => ({
    value: s.vehicleId,
    label: formatSuggestion(s),
  }));
}

export function formatSuggestion(suggestion) {
  return suggestion.trimName === suggestion.vehicleName
    ? `${suggestion.yearId} ${suggestion.makeName} ${suggestion.modelName} ${suggestion.trimName}`
    : ` ${suggestion.displayName}`;
}

export const createFunnelUrl = (funnelUrlParams: FunnelUrlParams) => {
  const { make, model, vin, offerCode, groupId, voiVin, dealerId, mileage, vehicleId, yearId, sessionId } = funnelUrlParams;
  const makeUrlFormatted = toUrlDisplayNamePath(make);
  const modelUrlFormatted = toUrlDisplayNamePath(model);
  let url = `${process.env.FURY_URL}/instant-cash-offer${yearId ? `/${yearId}` : ''}${makeUrlFormatted ? `/${makeUrlFormatted}` : ''}${modelUrlFormatted ? `/${modelUrlFormatted}` : ''}/?${groupId ? `groupid=${groupId}&` : ''}LNX=INTBANNER${!!sessionId ? `&sessionId=${sessionId}` : ''}${voiVin ? `&voivin=${voiVin}` : ''}`;
  if (dealerId) {
    url = `${url}&dealerid=${dealerId}`;
  }
  if (offerCode) {
    url = `${url}&offerCode=${offerCode}`;
  }
  if (vin || vehicleId) {
    const leadType = getLeadType(funnelUrlParams);
    const pa = dealerId ? `["${offerCode}","${dealerId}"]` : `["${offerCode}"]`;
    const jsonStr = `{"vin":"${vin}","p":1,"displayYMM":true,"pa":${pa},${leadType} "m":${mileage},"lp":1,"s":"${vehicleId}"}`;
    const hash = `#_____${toBase64(jsonStr)}`;
    url = `${url}${hash}`;
  }
  return url;
};

export const getLeadType = (funnelUrlParams: FunnelUrlParams) => {
  const { voiVin, dealerId, dealershipAcceptsRv, cobrandingGroupAcceptsRv } = funnelUrlParams;

  if (!dealerId || dealerId == GUID_EMPTY) {
    return `"qrv/LeadType":{"v":"CashOffer"},`;
  }
  const isAcceptRv = dealershipAcceptsRv && cobrandingGroupAcceptsRv;
  if (isAcceptRv && voiVin) {
    return `"qrv/LeadType":{"v":"OptIn"},`;
  }

  if (!isAcceptRv) {
    return `"qrv/LeadType":{"v":"OptOut"},`;
  }

  return '';
};

export const createFunnelAssignedOfferUrl = (funnelUrlParams: FunnelUrlParams) => {
  const { offerCode, dealerId, groupId, sessionId } = funnelUrlParams;
  const url = `${process.env.FURY_URL}/instant-cash-offer/?offerCode=${offerCode}&groupid=${groupId}&dealerid=${dealerId}&LNX=INTBANNER${!!sessionId ? `&sessionId=${sessionId}` : ''}`;
  return url;
};

export const createOfferCertificateUrl = (offerId, offerCode) => {
  return `${getOfferCertificateUrl(offerId)}?offerCode=${offerCode}&LNX=INTBANNER`;
};

export const getVinError = (errors: Set<string>): string => {
  return Object.values(VIN_ERRORS).find((err) => errors.has(err));
};

export const getLPError = (errors: Set<string>): string => {
  return Object.values(LICENSE_PLATE_ERROR).find((err) => errors.has(err));
};
export const getYMMError = (errors: Set<string>): string => {
  return Object.values(YMM_ERRORS).find((err) => errors.has(err));
};

export const getStyleError = (errors: Set<string>): string => {
  if (errors.has(MSM_STYLE_MISSING)) {
    return MSM_STYLE_MISSING;
  }
  return undefined;
};

export const getMileageError = (errors: Set<string>): string => {
  return Object.values(MSM_MILEAGE_ERRORS).find((err) => errors.has(err));
};

export const parseMileage = (m: string) => m.replace(/[^0-9]/g, '').replace(/^0/, '');

export const translateTabValue = (tabKey: string): string => {
  return tabKey === LICENSE_PLATE_KEY ? LICENSE_PLATE_VALUE : tabKey;
};

export const hasBoostAmount = (markupAmounts): boolean => {
  const markupAmount = calculateDealerBoost(markupAmounts);
  if (markupAmount && markupAmount > 0) {
    return true;
  }
  return false;
};

export type bannerSizeType = 'xsmall' | 'small' | 'medium' | 'large';
export type textResetSizeType = 'xs' | 'sm';

export const getBannerSize = (currentSize: ContainerSize, componentDirection: ComponentDirection = ComponentDirection.Default): bannerSizeType => {
  const size = currentSize.breakpoints;
  let sizeMode: bannerSizeType = 'large' as bannerSizeType;
  switch (componentDirection) {
    case ComponentDirection.Heading:
      if (!size['4xs']) {
        sizeMode = 'xsmall';
      } else if (size['4xs'] && !size['3xs']) {
        sizeMode = 'small';
      } else if (size['3xs'] && !size.xs) {
        sizeMode = 'medium';
      }
      break;
    default:
      if (!size['4xs']) {
        sizeMode = 'xsmall';
      } else if (size.xxs && !size.xs) {
        sizeMode = 'small';
      } else if (size.xs && !size.lg) {
        sizeMode = 'medium';
      }
  }
  return sizeMode;
};

export const bannerStatus = (offerCertificate) => {
  const reviewStatus = [OFFER_STATUS.FR, OFFER_STATUS.FR1, OFFER_STATUS.FR2, OFFER_STATUS.INSPECTION];
  if (offerCertificate.status === OFFER_STATUS.INELIGIBLE) {
    return BannerStep.NotQualify;
  } else if (offerCertificate.status === OFFER_STATUS.INSPECTION && offerCertificate.offerCode !== 'TC') {
    return BannerStep.Inspection;
  } else if (reviewStatus.includes(offerCertificate.status)) {
    return BannerStep.Review;
  } else if (offerCertificate.status === OFFER_STATUS.CLEARED) {
    return BannerStep.Eligible;
  }
  return BannerStep.ThankYou;
};
